import * as React from "react";
import { File as FileIcon, FileArchive } from "lucide-react";
import clsx from "clsx";
import { TableCell, TableRow } from "@/components/ui/table/table";
import { Progress } from "@/components/ui/progress";
import { FileWithProgress } from "@/views/drive/modals/uploadModal/types";
import { isRejectionCode } from "./errorMessages";
import { normalizeDisplayProgress } from "./helpers";
import { getFilePhaseLabel } from "./phaseLabels";

interface Props {
  fileWithProgress: FileWithProgress;
}

export const UploadModalFilesRow: React.FC<Props> = ({ fileWithProgress }) => {
  const fileType = fileWithProgress.file.name.split(".").pop() || "unknown";
  const pathParts = fileWithProgress.relativePath.split("/");
  const fileName = pathParts.at(-1) ?? fileWithProgress.relativePath;
  const directoryPath = pathParts.slice(0, -1).join("/");
  const isRejected = fileWithProgress.status === "error" && isRejectionCode(fileWithProgress.error_code);
  const phaseLabel = isRejected
    ? "Rejected"
    : getFilePhaseLabel(fileWithProgress.status, fileWithProgress.current_event);
  const displayedProgress = normalizeDisplayProgress(fileWithProgress);

  return (
    <TableRow className={fileWithProgress.error ? "bg-red-50/60" : ""}>
      <TableCell className="min-w-0 px-2.5 py-3 font-medium">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-100 text-blue-600">
            {fileType === "zip" ? <FileArchive className="h-4 w-4" /> : <FileIcon className="h-4 w-4" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-midnight-blue-500" title={fileName}>
              {fileName}
            </div>
            {directoryPath ? (
              <div className="mt-0.5 truncate text-xs font-normal text-muted-foreground" title={directoryPath}>
                {directoryPath}
              </div>
            ) : null}
            {fileWithProgress.error ? (
              <div className="mt-0.5 truncate text-xs font-normal text-destructive" title={fileWithProgress.error}>
                {fileWithProgress.error}
              </div>
            ) : null}
          </div>
        </div>
      </TableCell>
      <TableCell className="px-2.5 py-3">
        <span
          className={clsx("whitespace-nowrap text-xs font-medium", {
            "text-emerald-700": fileWithProgress.status === "completed",
            "text-destructive": fileWithProgress.status === "error",
            "text-midnight-blue-400": fileWithProgress.status !== "completed" && fileWithProgress.status !== "error",
          })}
        >
          {phaseLabel}
        </span>
        <Progress
          value={displayedProgress}
          className={clsx("mt-1.5 h-1 w-full bg-slate-200", {
            "[&>div]:bg-emerald-700": fileWithProgress.status === "completed",
            "[&>div]:bg-coral": fileWithProgress.status === "error",
            "[&>div]:bg-slate-300": fileWithProgress.status === "pending",
            "[&>div]:bg-blue-600": fileWithProgress.status === "uploading",
          })}
          aria-label={`Upload progress for ${fileWithProgress.relativePath}`}
        />
      </TableCell>
      <TableCell className="px-2.5 py-3 text-right">
        <span className="text-xs font-semibold tabular-nums text-midnight-blue-500">
          {displayedProgress.toFixed(0)}%
        </span>
      </TableCell>
    </TableRow>
  );
};
